import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	casesLawyerService,
	GET_LAWYER_CASES_LIST,
	GET_PINNED_CASES,
} from '@/api';
import { useNotification } from './useNotification';

export const useArchivedCases = () => {
	const queryClient = useQueryClient();
	const { toastSuccess, toastError } = useNotification();
	const pendingIdsRef = useRef<Set<string>>(new Set());
	const [, forceRender] = useState(0);

	const isPending = (caseId: string) => pendingIdsRef.current.has(caseId);

	const setPending = (caseId: string, value: boolean) => {
		const next = new Set(pendingIdsRef.current);
		value ? next.add(caseId) : next.delete(caseId);
		pendingIdsRef.current = next;
		forceRender((n) => n + 1);
	};

	const refetchCaseLists = () => {
		queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASES_LIST] });
		queryClient.refetchQueries({ queryKey: [GET_PINNED_CASES] });
	};

	const archiveCase = async (caseId: string) => {
		if (isPending(caseId)) return;
		setPending(caseId, true);
		try {
			await casesLawyerService.archiveCase(caseId);
			toastSuccess('This case has been successfully archived.');
			refetchCaseLists();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setPending(caseId, false);
		}
	};

	const unarchiveCase = async (caseId: string) => {
		if (isPending(caseId)) return;
		setPending(caseId, true);
		try {
			await casesLawyerService.unarchiveCase(caseId);
			toastSuccess('This case has been moved back to My Cases.');
			refetchCaseLists();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setPending(caseId, false);
		}
	};

	return {
		isPending,
		archiveCase,
		unarchiveCase,
	};
};
